"use client"

import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * What every filter chip is made of — canvas 7c's "Start · Apr – Jun 2026",
 * "Fee · $20k – $40k", wearing the three states turn 31a names for them.
 *
 * Resting, a chip is a quiet box. **Set**, it takes the accent border and wash
 * and states its value in mono, so a glance along the row says which filters
 * are on without reading any of them. **Open**, the chevron flips.
 *
 * The shell and the contents are separate exports rather than one component
 * because the chip is assembled differently depending on what it opens, and
 * `ChipPopover` is the one place that assembles it. Sharing the class list is
 * the point: two spellings of one object drift, and the row stops looking like
 * a row.
 */

const CHIP =
  "relative inline-flex flex-none items-center gap-2 rounded-md border px-3 py-2 transition-colors"

export function chipClass(active?: boolean, className?: string) {
  return cn(
    CHIP,
    active
      ? "border-primary/35 bg-primary/[0.07] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
      : "bg-card",
    className,
  )
}

export function ChipContent({
  label,
  value,
  active,
  open,
  mono = true,
}: {
  /** The quiet prefix. Omitted where the value names itself — "All companies". */
  label?: string
  /** What the filter is currently set to — "any" when it is not set. */
  value: string
  active?: boolean
  open?: boolean
  /**
   * Values are mono, which is what tells a figure from a word across this app.
   * A company's name is a word, so it opts out.
   */
  mono?: boolean
}) {
  return (
    <>
      {label && (
        <span className="text-[11px] leading-none text-muted-foreground">{label}</span>
      )}
      <span
        className={cn(
          "truncate leading-none",
          mono ? "whitespace-nowrap font-mono text-[12px]" : "text-[12.5px]",
          active ? "text-brand-ink" : "text-foreground",
        )}
      >
        {value}
      </span>
      <ChevronDown
        aria-hidden
        className={cn(
          "size-3.5 flex-none transition-transform",
          open ? "rotate-180 text-brand-ink" : "text-muted-foreground",
        )}
      />
    </>
  )
}
